import { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

/**
 * <TruncatedText> — cell that wraps to at most `lines` lines (2 by default)
 * within `maxWidth`, ellipsises whatever doesn't fit, and shows the full value
 * in a hover tooltip *only when the content is actually clipped*. Use anywhere
 * a table cell could overflow (admin listings, modals, summaries).
 *
 * Usage in a listing:
 *
 *   import TruncatedText, { TruncatedTextTooltip } from "../../../Components/TruncatedText/TruncatedText";
 *
 *   // ...inside the page JSX, mount the tooltip target ONCE per page:
 *   <TruncatedTextTooltip />
 *
 *   // Plain text cell — tooltip uses the same value as the visible text:
 *   <TruncatedText text={row.name} maxWidth={220} />
 *
 *   // Rich JSX cell (e.g. icon + label from MapProduct) — pass the JSX as
 *   // children for display, and `text` for the hover tooltip:
 *   <TruncatedText text={MapProduct(row.product, "simple")} maxWidth={COLUMN_WIDTHS.product}>
 *     {MapProduct(row.product)}
 *   </TruncatedText>
 *
 * Props:
 *   text       — string | number | null. The tooltip value. Also the display
 *                value when `children` is not provided. Falsy + no children
 *                renders an em-dash.
 *   children   — Optional ReactNode rendered as the visible content. The cell
 *                still clips at maxWidth; the tooltip uses `text`.
 *   maxWidth   — px (default 220). Cell wraps/clips beyond this width.
 *   lines      — max number of lines before clipping (default 2). Pass 1 for
 *                values that must never wrap.
 *   className  — extra classes to merge.
 *   mono       — render the text in a monospace <code> wrapper (for slugs/IDs).
 *                Ignored when `children` is provided.
 *   underline  — show the dotted underline hint. Only renders when the cell
 *                is actually overflowing (default false).
 */

export const TRUNCATED_TEXT_TOOLTIP_ID = "truncated-text-tooltip";

/**
 * Centralised column widths so every admin listing uses the same sizing.
 * Change a value here and every listing picks it up.
 *
 *   import { COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";
 *
 * Pattern:
 *   <TruncatedText text={r.email} maxWidth={COLUMN_WIDTHS.email} />
 *   { name: "Created At", minWidth: `${COLUMN_WIDTHS.date}px`, cell: r => <DateCell value={r.created_at} /> }
 */
export const COLUMN_WIDTHS = {
    // Identifier / slug cells
    refId: 160,
    actionKey: 240,
    slug: 200,

    // Text cells (truncate + tooltip)
    name: 180,
    title: 220,
    email: 220,
    organization: 200,
    country: 140,
    phone: 150,
    address: 260,
    subject: 320,
    notes: 260,
    description: 260,
    product: 220,
    machine: 200,
    code: 120,
    url: 260,

    // Datetime cells (minWidth so the value never wraps)
    date: 160,    // "Jan 5, 2026, 3:45 PM"
    dateShort: 120,    // "2026-01-05"
    relativeDate: 140,    // "2 days ago"
};

/**
 * Render a date/time string on a single line. Used together with a column
 * `minWidth: COLUMN_WIDTHS.date` so the column never shrinks below a width
 * that would force the value onto two lines.
 *
 * Pass `value` already-formatted (e.g. via ShowTime). Falsy renders an
 * em-dash.
 */
export function DateCell({ value, minWidth = COLUMN_WIDTHS.date }) {
    if (!value) return <span className="text-muted">—</span>;
    return (
        <span style={{ whiteSpace: "nowrap", display: "inline-block", minWidth }}>
            {value}
        </span>
    );
}

export default function TruncatedText({
    text,
    children,
    maxWidth = 220,
    lines = 2,
    className = "",
    mono = false,
    underline = false,
}) {
    const ref = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const hasChildren = children !== undefined && children !== null && children !== false;
    const tooltipValue = text === null || text === undefined ? "" : String(text);
    const isEmpty = !hasChildren && tooltipValue === "";

    // Measure overflow after layout. ResizeObserver catches column-width
    // changes from the table's responsive resizing; we re-run on text /
    // children / maxWidth / lines / mono changes so re-rendered rows reset
    // correctly. Height is what gives the clip away once the text is allowed to
    // wrap — width only still matters for the single-line (`lines={1}`) case.
    useLayoutEffect(() => {
        if (isEmpty) { setIsOverflowing(false); return; }
        const el = ref.current;
        if (!el) return;
        const measure = () => setIsOverflowing(
            el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
        );
        measure();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        } else {
            window.addEventListener("resize", measure);
        }
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", measure);
        };
    }, [text, children, maxWidth, lines, mono, isEmpty]);

    if (isEmpty) return <span className="text-muted">—</span>;

    const content = hasChildren
        ? children
        : (mono ? <code>{tooltipValue}</code> : tooltipValue);

    // Tooltip attaches only when (a) the cell is actually clipped AND (b) we
    // have a tooltip string to show. Cells that pass `children` without a
    // tooltip string just clip silently.
    const attachTooltip = isOverflowing && tooltipValue !== "";

    return (
        <span
            ref={ref}
            data-tooltip-id={attachTooltip ? TRUNCATED_TEXT_TOOLTIP_ID : undefined}
            data-tooltip-content={attachTooltip ? tooltipValue : undefined}
            className={className}
            style={{
                // -webkit-box + line-clamp is what puts the ellipsis at the end
                // of the *last* allowed line. Supported in every browser we
                // target, including Firefox.
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: lines,
                maxWidth,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "normal",
                // Long unbroken values (emails, reference ids) have no spaces to
                // wrap on, so let them break mid-token and fill the second line
                // instead of clipping the first one.
                overflowWrap: "anywhere",
                verticalAlign: "bottom",
                borderBottom: underline && isOverflowing ? "1px dotted #cbd5e1" : "none",
            }}
        >
            {content}
        </span>
    );
}

/**
 * Mount once per page (next to other react-tooltip instances). Renders the
 * shared `<Tooltip>` that every <TruncatedText> on the page attaches to.
 *
 * Multiple page-level mounts are harmless but wasteful — keep it to one.
 */
export function TruncatedTextTooltip(props) {
    return (
        <Tooltip
            id={TRUNCATED_TEXT_TOOLTIP_ID}
            place="top"
            delayShow={120}
            // Keep the tooltip alive while the cursor is inside it so the user
            // can select and copy long values. `delayHide` covers the gap as
            // the cursor moves from the cell into the popup.
            clickable
            delayHide={120}
            style={{
                zIndex: 9999,
                maxWidth: 480,
                whiteSpace: "normal",
                wordBreak: "break-word",
                fontSize: 13,
                lineHeight: 1.4,
                // Make the contents selectable. react-tooltip's default inner
                // styles set user-select: none on hover-mode tooltips.
                userSelect: "text",
                WebkitUserSelect: "text",
                cursor: "text",
            }}
            {...props}
        />
    );
}
